<script lang='ts'>
    import { round } from "@/Util/utils";
    import HelpTooltip from "@/Common/HelpTooltip.svelte";

    export let training_advice

    let progressIndicatorDegree = 2
    let progressIndicatorTomorrowDegree = 2
    let progressIndicatorTomorrowColor = "#FFFFFF"

    $: if (training_advice) {
        const progressIndicator = training_advice.progress_indicator ?? -1
        progressIndicatorDegree = Math.min(Math.max((90 + (90 * progressIndicator)), 2), 178) // Convert [-1, 1] to [0, 180], and then minmax the range

        const progressIndicatorTomorrow = training_advice.progress_indicator_tomorrow ?? -1
        progressIndicatorTomorrowDegree = Math.min(Math.max((90 + (90 * progressIndicatorTomorrow)), 2), 178) // Convert [-1, 1] to [0, 180], and then minmax the range

        progressIndicatorTomorrowColor = training_advice.tomorrow_status?.color ?? "#FFFFFF"
    }

    const colors = {
        gray: '#6f7276',
        red: '#ff526c',
        blue: '#5ed8e0',
    }

    type Band = {
        start: number
        end: number
        color: string
    }
    type Axis = {
        ringRadius: number
        bands: Band[]
    }

    const vh = 100
    const vw = vh*2

    const needleAxleSize = 16
    const ringWidth = 13

    const dialCX = vw/2
    const dialCY = vh - (needleAxleSize/2)

    const outerRingRadius = vh - (ringWidth/2) - (needleAxleSize/2) - 2
    const innerRingRadius = outerRingRadius - ringWidth - 2

    /**
     * @param value Value in range -1,1 will be automatically converted to degrees
     */
    const pathCoordsFromValue = (value: number, radius: number) => {
        const angle = 90 + (value * 90 * -1) // Normalizes [-1, 1] to [180, 0]
        const radians = angle * Math.PI / 180
        const x = round(radius * Math.cos(radians))
        const y = round(radius * Math.sin(radians))
        return `${x},${y * -1}`
    }

    // CHART DECLARATIONS
    const trainingNeededAxis: Axis = {
        ringRadius: outerRingRadius,
        bands: [
            {start: -1, end: 0.1, color: colors.gray},
            {start: 0.116, end: 1, color: colors.blue},
        ]
    }

    const onPaceAxis: Axis = {
        ringRadius: innerRingRadius,
        bands: [
            {start: -1, end: -0.4, color: colors.red},
            {start: -0.386, end: 0.6, color: colors.gray},
            {start: 0.614, end: 1, color: colors.blue},
        ]
    }

    const allAxes = [trainingNeededAxis, onPaceAxis]
</script>

<div class="min-w-[200px]">
    <div class="aspect-[2/1] w-full relative">
        <svg
            class="absolute left-0 top-0 w-full h-full"
            viewBox="0 0 {vw} {vh}"
        >
            {#each allAxes as {ringRadius, bands}}
                <!-- Ring -->
                <g fill='none' stroke-width={ringWidth} transform="translate({dialCX}, {dialCY})">
                    {#each bands as {start, end, color}}
                        <!-- Each segment -->
                        <path d="M {pathCoordsFromValue(start, ringRadius)} A {ringRadius},{ringRadius} 0 0,1 {pathCoordsFromValue(end, ringRadius)}" stroke={color}/>
                    {/each}
                </g>
            {/each}

            <!-- Dot at the base of the dial (looks like the needle's axle) -->
            <circle
                cx={dialCX}
                cy={dialCY}
                r={needleAxleSize/2}
                fill="black"
                stroke-width={0}
            />

            <g fill="black" stroke-width={0} transform="translate({dialCX}, {dialCY})">
                <!-- Tomorrow Colored Triangle -->
                <path class="animate-rotation" style="transform: rotate({progressIndicatorTomorrowDegree}deg);" fill={progressIndicatorTomorrowColor} d="M {pathCoordsFromValue(-1, innerRingRadius - needleAxleSize/2 - 2)} L {pathCoordsFromValue(-1.14, innerRingRadius - needleAxleSize/2 - 14)} L {pathCoordsFromValue(-0.86, innerRingRadius - needleAxleSize/2 - 14)} Z" />
                <!-- Main Needle -->
                <path class="animate-rotation" style="transform: rotate({progressIndicatorDegree}deg);" d="M {pathCoordsFromValue(-1.3, needleAxleSize/2)} L {pathCoordsFromValue(-1.01, innerRingRadius)} L {pathCoordsFromValue(-0.99, innerRingRadius)} L {pathCoordsFromValue(-0.7, needleAxleSize/2)} Z" />
            </g>
        </svg>
    </div>
    
    <div class="w-full flex items-center justify-center gap-1 font-bold text-lg mt-3">
        Training Pacer
        <HelpTooltip message="Shows whether you're on pace with your training program today and tomorrow. You should train enough to move the needle to the right of center every few days to stay on track with your set Improvement Rate. <a href='https://www.baronbiosys.com/glossary/xert-training-pacer/' target='_help'>Learn more</a>" />
    </div>
</div>

<style lang="scss">
    .animate-rotation {
        transition: transform 0.5s cubic-bezier(0.19, 1, 0.22, 1);
    }
</style>